import { ONE_DAY_IN_MILLISECONDS } from '../hooks/rentalExtension';
import { APP_STATE } from '../webStorage';
import { getPersistenceEnabled, setPersistenceEnabled } from './cookies';
import { decodeJWT, jwtExpired } from './jwt';
import logger from './logger';

/**
 * Checks whether the WebApp client is running in development mode. Both localhost and .dev domains are considered development.
 * @returns True if the app runs in dev mode, false otherwise (UAT, PROD, etc.).
 */
export const isDevEnv = () => {
  if (typeof window === 'undefined' || !window.location) {
    return false;
  }

  return ['localhost', '127.0.0.1'].includes(window.location.hostname) || window.location.hostname.includes('.dev.');
};

export const disablePersistenceAndClearStateFromStorage = session => {
  setPersistenceEnabled(session, false);
  localStorage.removeItem(APP_STATE);
};

export const enablePersistence = session => {
  setPersistenceEnabled(session, true);
};

const canPersist = session => getPersistenceEnabled(session);

const decodeStoredPayload = stored => {
  const { data, expiresAt, paused } = JSON.parse(stored);
  if (Date.now() > expiresAt) {
    localStorage.removeItem(APP_STATE);
    return null;
  }
  const state = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(data), c => c.charCodeAt(0))));
  return { state, expiresAt, paused };
};

const encodeAndSaveState = (state, expiresAt, paused = false) => {
  const encodedState = new TextEncoder().encode(JSON.stringify(state));
  const base64State = btoa(encodedState.reduce((acc, byte) => acc + String.fromCharCode(byte), ''));
  const payload = { data: base64State, expiresAt };
  if (paused) {
    payload.paused = true;
  }
  localStorage.setItem(APP_STATE, JSON.stringify(payload));
};

/**
 * Persists the given state to localStorage if the session allows persistence.
 *
 * The state is first JSON-stringified, then encoded as a Uint8Array using TextEncoder,
 * then converted to a base64 string. The payload includes the encoded state and an
 * expiration timestamp set to one day from the current time.
 *
 * @param {Object} session - The current session object used to determine if persistence is allowed.
 * @param {Object} state - The application state to be persisted.
 */
export const persistStore = (session, state) => {
  if (!canPersist(session)) {
    return;
  }

  try {
    encodeAndSaveState(state, Date.now() + ONE_DAY_IN_MILLISECONDS);
  } catch (err) {
    logger.warn('Failed to persist Redux state:', err);
  }
};

/**
 * Pauses persistence when the JWT expires mid-flow.
 *
 * Disables the persistence cookie (temporarily pauses writing/overwrite to the saved state) and marks the stored
 * payload with `paused: true` so that `fromBase64Payload` returns null on next app
 * load, preventing the stale mid-flow page from being restored. The rest of the
 * state is kept intact so it can be resumed once a new JWT is obtained.
 *
 * @param {Object} session - The current session object.
 */
export const pauseStatePersistence = session => {
  try {
    if (!getPersistenceEnabled(session)) {
      return;
    }
    setPersistenceEnabled(session, false);
    const stored = localStorage.getItem(APP_STATE);
    if (!stored) {
      return;
    }
    const decoded = decodeStoredPayload(stored);
    if (!decoded) {
      return;
    }
    const { state, expiresAt } = decoded;
    encodeAndSaveState(state, expiresAt, true);
  } catch (err) {
    logger.warn('Failed to pause state persistence:', err);
  }
};

/**
 * Resumes persistence after a successful JWT renewal.
 *
 * Re-enables the persistence cookie, writes the new JWT into the stored state,
 * and clears the `paused` flag so the state is eligible for restoration on the
 * next app load. Preserves the original expiration timestamp.
 *
 * Always re-enables the cookie first so that even if no stored state is found,
 * normal persistence can resume.
 *
 * Writes the new JWT and clears the paused flag but leaves the persistence cookie disabled.
 * This prevents persistStore from overwriting this state before the page reload that follows
 *  JWT_CREATED (actions.coreApi.refresh calls window.location.reload).
 * The cookie is re-enabled in app.js when the restored state is successfully loaded on the next page boot.
 *
 * @param {Object} session - The current session object.
 * @param {string} newJwt - The renewed JWT string to write into the stored state.
 */
export const resumeStatePersistence = (session, newJwt) => {
  try {
    const stored = localStorage.getItem(APP_STATE);
    if (!stored) {
      return;
    }
    const decoded = decodeStoredPayload(stored);
    if (!decoded) {
      return;
    }
    const { state, expiresAt } = decoded;
    encodeAndSaveState({ ...state, coreApi: { ...state.coreApi, jwt: newJwt } }, expiresAt);
  } catch (err) {
    logger.warn('Failed to resume state persistence:', err);
  }
};

/**
 * Decodes and retrieves a persisted state object from a base64-encoded payload.
 *
 * @param {string} stored - The JSON string containing the base64-encoded data and expiration timestamp.
 * @returns {Object|null} The decoded state object if valid and not expired, otherwise null.
 *
 * @throws Will log a warning and return null if decoding or parsing fails.
 */
export const fromBase64Payload = stored => {
  try {
    const decoded = decodeStoredPayload(stored);
    if (!decoded || decoded.paused) {
      return null;
    }
    return decoded.state;
  } catch (err) {
    logger.warn('Failed to decode persisted state:', err);
    return null;
  }
};

/**
 * Resolves the persisted state on app boot.
 *
 * Reads the stored payload, checks whether the JWT has expired, and handles
 *  the persistence cookie accordingly:
 * - Expired JWT: pauses persistence (keeps state in localStorage for later resumption)
 * - Valid state: re-enables the persistence cookie (resumeStatePersistence leaves it disabled)
 *
 * @param {string|null} localState - The raw localStorage value for APP_STATE.
 * @returns {Object|null} The decoded state to hydrate the app with, or null if absent/expired.
 */
export const resolvePersistedState = localState => {
  const decoded = localState && fromBase64Payload(localState);
  const isExpired = decoded && jwtExpired(decodeJWT(decoded.coreApi?.jwt));

  if (isExpired) {
    pauseStatePersistence(decoded.global?.session);
  } else if (decoded) {
    enablePersistence(decoded.global?.session);
  }

  return isExpired ? null : decoded;
};
